import { useRudderAnalytics } from '@portal/shared/providers/RudderAnalytics';
import { newsletterSignupEvent } from 'analytics/events/studio';
import { useCallback, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { getISOLanguage } from 'shared/utils/locale';
import { useLanguageValue } from 'state/hooks/useLanguage';
import { useStoreValue } from 'state/hooks/useStore';
import { useMutationKlaviyo } from 'state/internal/klaviyo/klaviyo';
import { ArticleEmailSubscription as ArticleEmailSubscriptionType } from 'types/studio/studio';
import styles from './EduArticleEmailSubscriptionForm.module.scss';
import Input from './Input/Input';

type FieldValues = {
  email: string;
};

const validateEmail = (email: string) => {
  if (!email) {
    return 'Please enter your e-mail address.';
  }
  if (!/^\S+@\S+\.\S+$/.test(email)) {
    return 'You have entered an invalid e-mail address. Please try again.';
  }
  return false;
};

type DataType = {
  data: { sectionType: string } & Pick<
    ArticleEmailSubscriptionType,
    'label' | 'placeholder' | 'buttonLabel' | 'successMessage' | 'errorMessage'
  >;
};

const EduArticleEmailSubscriptionForm = ({ data }: DataType) => {
  const rudderAnalytics = useRudderAnalytics();
  const { sectionType, label, placeholder, buttonLabel, successMessage, errorMessage } = data;
  const store = useStoreValue();
  const language = useLanguageValue();
  const { mutate, isPending } = useMutationKlaviyo();
  const [isSuccess, setIsSuccess] = useState(false);

  const title = label.toString();

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
    watch
  } = useForm<FieldValues>({
    defaultValues: {
      email: ''
    }
  });

  const emailState = watch('email');

  useEffect(() => {
    const emailError = errors.email?.message;
    if (emailError) {
      newsletterSignupEvent({
        store,
        success: false,
        title,
        email: emailState,
        location: 'footer',
        errorMessage: emailError
      });
    }
  }, [errors, title, emailState, store]);

  const handleSubmitValid: SubmitHandler<FieldValues> = useCallback(
    ({ email }) => {
      const emailValidationError = validateEmail(email);
      if (emailValidationError) {
        setError('email', { type: 'manual', message: emailValidationError });
        return;
      }
      mutate(
        { email, store, language: getISOLanguage(language, store) },
        {
          onError: () => {
            setError('email', {
              type: 'custom',
              message: errorMessage
            });
            newsletterSignupEvent({ store, success: false, title, location: 'article', email });
          },
          onSuccess: () => {
            setIsSuccess(true);

            if (email) {
              rudderAnalytics?.identify({
                store,
                email,
                success: true,
                title,
                location: 'article'
              });
            }

            newsletterSignupEvent({ store, success: true, title, location: 'article', email });
          }
        }
      );
    },
    [errorMessage, language, mutate, setError, store, title, rudderAnalytics]
  );

  return (
    <form className={styles.form} onSubmit={handleSubmit(handleSubmitValid)}>
      <Controller
        control={control}
        name="email"
        render={({ field }) => (
          <Input
            {...field}
            placeholder={placeholder || ''}
            buttonLabel={buttonLabel}
            successMessage={successMessage}
            isLoading={isPending}
            errors={errors}
            isSuccess={isSuccess}
            sectionType={sectionType}
          />
        )}
      />
    </form>
  );
};

export default EduArticleEmailSubscriptionForm;
